import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, KeyRound, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ChangePassword() {
  const [currentPw, setCurrentPw] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = currentPw && pw.length >= 6 && pw === pw2 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: pw,
      });
      toast.success("Password updated");
      setCurrentPw("");
      setPw("");
      setPw2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <Link
        to="/app/settings"
        className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-900 mb-3"
      >
        <ChevronLeft size={14} /> Settings
      </Link>
      <div className="mb-6">
        <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Change Password</h1>
        <p className="text-[14px] text-slate-500 mt-1">
          Update the password you use to sign in. You'll stay signed in on this device.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 max-w-lg space-y-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
            Current password
          </label>
          <input
            type="password"
            required
            autoFocus
            autoComplete="current-password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
            New password
          </label>
          <input
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            placeholder="At least 6 characters"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
            Confirm new password
          </label>
          <input
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className={
              "w-full h-10 px-3 border rounded-lg text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition " +
              (pw2 && pw !== pw2 ? "border-red-400" : "border-slate-200")
            }
          />
          {pw2 && pw !== pw2 && (
            <p className="text-[11.5px] text-red-600 mt-1">Passwords don't match.</p>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-10 px-5 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition disabled:opacity-60 active:scale-95"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {busy ? "Updating…" : "Update password"}
          </button>
        </div>
        <p className="text-[11.5px] text-slate-400">
          Changing your password also invalidates any outstanding password-reset links for your account.
        </p>
      </form>
    </div>
  );
}
